import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { url } from '../../utils/constants';
import { toast } from 'sonner';

export const useAutoStatusUpdate = (transactions, refetch) => {
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const intervalRef = useRef(null);
  const previousStatusRef = useRef({});
  const lastNotificationRef = useRef({});

  // Função para verificar atualizações de status
  const checkStatusUpdates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      // Buscar transações pendentes
      const pendingTransactions = transactions.filter(t => t.status === 'Pendente');

      if (pendingTransactions.length === 0) {
        setIsPolling(false);
        return;
      }

      let hasStatusChanged = false;

      // Verificar status de cada transação pendente
      for (const transaction of pendingTransactions) {
        try {
          const response = await axios.get(
            `${url}/api/v1/pagamento/${transaction._id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          const updatedTransaction = response.data.data || response.data;
          const newStatus = updatedTransaction.status;
          const previousStatus = previousStatusRef.current[transaction._id];

          // Se o status mudou, mostrar notificação (evitar duplicatas)
          if (previousStatus && previousStatus !== newStatus && newStatus) {
            hasStatusChanged = true;
            const notificationKey = `${transaction._id}_${newStatus}`;
            const now = Date.now();

            // Evitar notificações duplicadas em menos de 5 segundos
            if (!lastNotificationRef.current[notificationKey] ||
              now - lastNotificationRef.current[notificationKey] > 5000) {

              const statusMessages = {
                'PAGO': 'Pagamento confirmado!',
                'Falhou': 'Pagamento falhou',
                'Expirado': 'Pagamento expirado'
              };

              const message = statusMessages[newStatus] || `Status alterado para ${newStatus}`;

              if (newStatus === 'PAGO') {
                toast.success(`💰 ${message} - R$ ${parseFloat(transaction.amount).toFixed(2).replace('.', ',')}`);
              } else {
                toast.info(`ℹ️ ${message}`);
              }

              lastNotificationRef.current[notificationKey] = now;
            }
          }

          // Atualizar referência do status anterior
          previousStatusRef.current[transaction._id] = newStatus;

        } catch (error) {
          console.error(`Erro ao verificar status da transação ${transaction._id}:`, error);
        }
      }

      // Só recarregar dados se houve mudanças de status
      if (hasStatusChanged) {
        console.log('🔄 Status alterado, recarregando dados...');
        refetch();
      }

    } catch (error) {
      console.error('Erro ao verificar atualizações de status:', error);
    }
  }, [transactions, refetch]);

  // Iniciar polling automático
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Já está rodando

    setIsPolling(true);

    // Verificar imediatamente
    checkStatusUpdates();

    // Verificar a cada 30 segundos
    intervalRef.current = setInterval(checkStatusUpdates, 30000);
    console.log('🔄 Polling iniciado - verificando a cada 30 segundos');
  }, [checkStatusUpdates]);

  // Parar polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('⏹️ Polling parado');
    }
    setIsPolling(false);
  }, []);

  // Inicializar referências de status
  useEffect(() => {
    transactions.forEach(transaction => {
      if (!previousStatusRef.current[transaction._id]) {
        previousStatusRef.current[transaction._id] = transaction.status;
      }
    });
  }, [transactions]);

  // Controlar polling baseado nas transações - VERSÃO CORRIGIDA
  useEffect(() => {
    const hasPendingTransactions = transactions.some(t => t.status === 'Pendente');
    const pendingCount = transactions.filter(t => t.status === 'Pendente').length;

    console.log('🔍 Controle de polling:', {
      hasPendingTransactions,
      isPolling,
      totalTransactions: transactions.length,
      pendingCount
    });

    // Só iniciar polling se há transações pendentes E não está rodando
    if (hasPendingTransactions && !isPolling) {
      console.log('▶️ Iniciando polling - há transações pendentes');
      startPolling();
    }

    // Só parar polling se não há transações pendentes E está rodando
    if (!hasPendingTransactions && isPolling) {
      console.log('⏹️ Parando polling - não há transações pendentes');
      stopPolling();
    }

    // Cleanup ao desmontar
    return () => {
      if (isPolling) {
        stopPolling();
      }
    };
  }, [transactions.length, isPolling]); // Só depende do tamanho da lista e status do polling

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []); // Dependências vazias para executar apenas no unmount

  return {
    isPolling,
    startPolling,
    stopPolling
  };
};
